import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useToast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/ui/use-toast";
import { mxValidationsApi } from "./api";
import { Search } from "lucide-react";
import { useState, useCallback } from "react";

// Add your feature-specific hooks here
export const useValidationBreakRunCount = (
  meterRunId: string,
  page: number,
  limit: number,
  remoteUtilityId: string
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.validations.validationBreakRunCount(
      meterRunId,
      page
    ),
    () =>
      mxValidationsApi.getValidationBreakRunCount(
        meterRunId,
        page,
        limit,
        remoteUtilityId
      ),
    {
      enabled: !!meterRunId && !!remoteUtilityId,
      refetchOnWindowFocus: false,
    }
  );
};

export const useNewMeterReadings = ({
  remoteUtilityId,
  meterRunId,
  page,
  limit,
  searchData = "",
  premises,
  sortBy,
  readingId,
}: {
  remoteUtilityId: string;
  meterRunId: string;
  page: number;
  limit: number;
  searchData?: string;
  premises?: string[];
  sortBy?: string[];
  readingId?: any;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.validations.newMeterReadings(
      meterRunId,
      page,
      searchData,
      premises,
      sortBy,
      readingId
    ),
    () =>
      mxValidationsApi.getNewMeterReadings({
        remoteUtilityId,
        meterRunId,
        page,
        limit,
        searchData,
        premises,
        sortBy,
      }),
    {
      enabled: !!meterRunId,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    }
  );
};

export const useRoutesList = (cycleId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.validations.routesList(cycleId),
    () => mxValidationsApi.getRoutesList(cycleId),
    {
      enabled: Boolean(cycleId),
      staleTime: 30 * 60 * 1000, // 30 minutes in milliseconds
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
};

export const useReadingsList = ({
  remoteUtilityId,
  meterRunId,
  page,
  limit,
  ruleStatus,
  searchData,
  premises,
  sortBy,
  readingId,
}: {
  remoteUtilityId: string;
  meterRunId: string;
  page: number;
  limit: number;
  ruleStatus?: string;
  searchData?: string;
  premises?: string[];
  sortBy?: string[];
  readingId?: any;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.validations.viewReadingsList(
      meterRunId,
      ruleStatus,
      page,
      searchData,
      premises,
      sortBy,
      readingId
    ),
    () =>
      mxValidationsApi.getReadings({
        remoteUtilityId,
        meterRunId,
        page,
        limit,
        ruleStatus,
        searchData,
        premises,
        sortBy,
      }),
    {
      staleTime: 1000,
      enabled: Boolean(meterRunId && remoteUtilityId),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
    }
  );
};

export const useMissingReadingsList = ({
  remoteUtilityId,
  meterRunId,
  page,
  limit,
  searchData,
  premises,
  sortBy,
}: {
  remoteUtilityId: string;
  meterRunId: string;
  page: number;
  limit: number;
  searchData?: string;
  premises?: string[];
  sortBy?: string[];
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.validations.missingReadingsList(
      page,
      searchData,
      premises,
      sortBy
    ),
    () =>
      mxValidationsApi.getMissingReadingsList({
        remoteUtilityId,
        meterRunId,
        page,
        limit,
        searchData,
        premises,
        sortBy,
      }),
    {
      enabled: Boolean(meterRunId && remoteUtilityId),
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
};

export const useMissingReadingsExport = (
  remoteUtilityId: string,
  meterRunId: string,
  searchData?: string,
  premises?: string[],
  sortBy?: string[]
) => {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const exportCSV = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const response = await mxValidationsApi.exportMissingReadingsList({
        page: undefined,
        remoteUtilityId,
        meterRunId,
        searchData,
        premises,
        sortBy
      });

      toast({
        title: "Export Initiated",
        description: response?.message,
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: `Failed to export missing readings: ${error?.message || "Something went wrong"}`,
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setIsExporting(false), 0);
    }
  }, [
    remoteUtilityId,
    meterRunId,
    searchData,
    premises,
    sortBy,
    isExporting,
    toast,
  ]);

  return { exportCSV, isExporting };
};

export const useExemptionList = ({
  meterRunId,
  page,
  limit,
  remoteUtilityId,
  searchData,
  premises,
  sortBy,
  readingId,
}: {
  meterRunId: string;
  page: number;
  limit: number;
  remoteUtilityId: string;
  searchData?: string;
  premises?: string[];
  sortBy?: string[];
  readingId?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.validations.exemptionList(
      meterRunId,
      page,
      searchData,
      premises,
      sortBy,
      readingId
    ),
    () =>
      mxValidationsApi.getExemptionsReadingList({
        meterRunId,
        page,
        limit,
        remoteUtilityId,
        searchData,
        premises,
        sortBy,
      }),
    {
      enabled: Boolean(meterRunId && remoteUtilityId),
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      retry: false,
    }
  );
};

export const useMeterReadingDetail = ({
  remoteUtilityId,
  meterRunId,
  meterReadingId,
  validatorId,
}: {
  remoteUtilityId: string;
  meterRunId: string;
  meterReadingId: string;
  validatorId: any;
}) => {
  console.log("useMeterReadingDetail params →", {
    validatorId,
    meterReadingId,
  });

  return useSmartQuery(
    QueryKeyFactory.module.mx.validations.viewMeterReadingDetail(meterReadingId),
    () =>
      mxValidationsApi.getMeterReadingDetail(
        remoteUtilityId,
        meterRunId,
        meterReadingId,
        validatorId
      ),
    {
      enabled: Boolean(meterReadingId && validatorId),
      refetchOnMount: false,
      staleTime: 0,
    }
  );
};


export const useMeterReadingConsumption = ({
  remoteUtilityId,
  meterNumber,
}: {
  remoteUtilityId: string;
  meterNumber: string;
}) =>
  useSmartQuery(
    QueryKeyFactory.module.mx.validations.meterReadingConsumption(meterNumber),
    () =>
      mxValidationsApi.getMeterReadingConsumption(remoteUtilityId, meterNumber),
    {
      enabled: !!remoteUtilityId && !!meterNumber,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 0,
    }
  );

export const usePreviousReadingImage = ({
  remoteUtilityId,
  readingId,
  showPrevious,
}: {
  remoteUtilityId: string;
  readingId: string;
  showPrevious: boolean;
}) =>
  useSmartQuery(
    QueryKeyFactory.module.mx.validations.previousReadingImage(readingId),
    () => mxValidationsApi.getPreviousImage(remoteUtilityId, readingId),
    {
      enabled: Boolean(readingId && remoteUtilityId && showPrevious),
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

export const useValidationCodes = (remoteUtilityId: string) =>
  useSmartQuery(
    QueryKeyFactory.module.mx.validations.validationCodes(),
    () => mxValidationsApi.getValidationCodes(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 30 * 60 * 1000, // 30 minutes
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

export const usePutAdjustReadings = (meterReadingId, meterRunId) => {
  return useSmartMutation(
    (data: any) =>
      mxValidationsApi.updateMeterReadingAdjustment(meterReadingId, data),
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: ["mx", "validation-break-run-count"],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "validation-break-list"],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "view-readings-list"],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "exemptions"],
        });
      },
      onError: () => {
        toast({
          variant: "destructive",
          title: "Error",
          description:
            "You are not an authorized person to PASS, REVISIT, EXEMPT or ADJUST",
        });
      },
    }
  );
};

export const usePutPassExemptReading = (meterReadingId, meterRunId) => {
  return useSmartMutation(
    (data: any) =>
      mxValidationsApi.updatePassExemptReading(meterReadingId, data),
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: ["mx", "validation-break-run-count"],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "view-readings-list"],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "exemptions"],
        });
         queryClient.refetchQueries({
          queryKey: ["mx", "validation-break-list"],
        });
      },
      onError: (error) => {
        toast({
          variant: "destructive",
          description:
            "You are not an authorized person to PASS, REVISIT, EXEMPT or ADJUST",
        });
      },
    }
  );
};

export const useReasonCodeList = (codeId: any) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.validations.reasonCodeList(codeId),
    () => mxValidationsApi.getReasonCodeList(remoteUtilityId, codeId),
    {
      enabled: !!remoteUtilityId && codeId !== null && codeId !== undefined,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
};

export const usePutMeterReadingRevisit = (meterReadingId: string) =>
  useSmartMutation((payload: any) =>
    mxValidationsApi.putMeterReadingRevisit(meterReadingId, payload,
    ),
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: ["mx", "validation-break-run-count"],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "view-readings-list"],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "exemptions"],
        });
      },
      onError: (error) => {
        toast({
          variant: "destructive",
          description:
            "You are not an authorized person to PASS, REVISIT, EXEMPT or ADJUST",
        });
      },
    }
  );

export const usePutMeter = () =>
  useSmartMutation(mxValidationsApi.putMeter, {
    onSuccess: () => {
      toast({
        variant: "default",
        title: "Success",
        description: "Meter added successfully",
      });
      queryClient.refetchQueries({
        queryKey: ["mx", "new-meter-readings"],
      });
      queryClient.refetchQueries({
        queryKey: ["mx", "validation-break-run-count"],
      });
      queryClient.refetchQueries({
        queryKey: ["mx", "view-readings-list"],
      });
      queryClient.refetchQueries({
        queryKey: ["mx", "exemptions"],
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

export const useGetAllSOPs = ({
  remoteUtilityId,
}: {
  remoteUtilityId: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.validations.sops(),
    () => mxValidationsApi.getAllSOPs({ remoteUtilityId }),
    {
      enabled: !!remoteUtilityId,
      refetchOnMount: false,
    }
  );
};

export const useValidationBreakSummary = (remoteUtilityId: string) =>
  useSmartQuery(
    QueryKeyFactory.module.mx.validations.validationBreakSummary(),
    () => mxValidationsApi.getValidationBreakSummary(remoteUtilityId),
    {
      staleTime: 30 * 60 * 1000, // 30 min
    }
  );

export const useValidationBreakList = ({
  remoteUtilityId,
  page,
  limit,
  filters = {},
  search,
  isCompleted,
}: {
  remoteUtilityId: string;
  page: number;
  limit: number;
  filters?: Record<string, any>;
  search?: string;
  isCompleted?: boolean;
}) =>
  useSmartQuery(
    QueryKeyFactory.module.mx.validations.viewValidationBreakList(
      page,
      filters,
      limit,
      search,
      isCompleted
    ),
    () =>
      mxValidationsApi.getActiveCyclesList(
        remoteUtilityId,
        page,
        limit,
        filters,
        search,
        isCompleted
      ),
    {
      staleTime: 0,
    }
  );
export const useValidationRules = (remoteUtilityId: string, open: boolean) =>
  useSmartQuery(
    QueryKeyFactory.module.mx.validations.utilityRules(),
    () => mxValidationsApi.getValidationRules(remoteUtilityId),
    { staleTime: 0, enabled: !!remoteUtilityId && !!open }
  );

export const usePutValidationRule = () =>
  useSmartMutation(mxValidationsApi.putValidationRule, {
    onSuccess: () => {
      toast({
        variant: "default",
        title: "Success",
        description: "Rule updated successfully",
      });
      queryClient.refetchQueries({
        queryKey: ["mx", "validation-rules"],
      });
    },
  });

export const useMeterCycleAuditUserMapping = (
  remoteUtilityId: string,
  search: string,
  open: boolean
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.validations.meterCycleValidators(search),
    () =>
      mxValidationsApi.getMeterCycleAuditUserMapping(remoteUtilityId, search),
    { staleTime: 0, enabled: !!remoteUtilityId && !!open }
  );
};

export const useAuditUsers = (
  remoteUtilityId: string,
  userType: number,
  open: boolean,
  cycleId?: number,
  search?: string
) => {
  const queryKey = QueryKeyFactory.module.mx.validations.auditUsers(userType, cycleId, search);
  return useSmartQuery(
    QueryKeyFactory.module.mx.validations.auditUsers(userType, cycleId, search),
    () =>
      mxValidationsApi.getAuditUsers(
        remoteUtilityId,
        userType,
        cycleId,
        search
      ),
    { staleTime: 0, enabled: !!remoteUtilityId && !!open }
  );
};

export const usePostMeterCycleAuditUserMapping = (
  userType?: number,
  cycleId?: number
) =>
  useSmartMutation(mxValidationsApi.postMeterCycleAuditUserMapping, {
    onSuccess: () => {
      const invalidateKey = QueryKeyFactory.module.mx.validations.auditUsers(0, cycleId);
      toast({
        variant: "default",
        title: "Success",
        description: "User updated successfully",
      });

      queryClient.refetchQueries({ queryKey: QueryKeyFactory.module.mx.validations.auditUsers(0, cycleId) });
      queryClient.refetchQueries({ queryKey: QueryKeyFactory.module.mx.validations.auditUsers(2, cycleId) });
      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.mx.validations.viewValidationBreakList(1, {}, 10),
        exact: false,
      });
      queryClient.refetchQueries({queryKey: QueryKeyFactory.module.mx.validations.meterCycleValidators("")})
    },
  });

export const usePutMeterCycleAuditUserMapping = (
  userType?: number,
  cycleId?: number
) =>
  useSmartMutation(mxValidationsApi.putMeterCycleAuditUserMapping, {
    onSuccess: () => {
      toast({
        variant: "default",
        title: "Success",
        description: "User removed successfully",
      });
      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.mx.validations.auditUsers(
          userType,
          cycleId
        ),
      });
    },
  });

export const usePutValidationCode = () =>
  useSmartMutation(
    (variables: { id: number; remoteUtilityId: string; payload: any }) =>
      mxValidationsApi.putValidationCode(
        variables.id,
        variables.remoteUtilityId,
        variables.payload
      ),
    {
      onSuccess: () => {
        toast({
          variant: "default",
          title: "Success",
          description: "Code edited successfully.",
        });

        queryClient.refetchQueries({
          queryKey: ["mx", "validation-code"],
        });
      },
    }
  );

export const usePostValidationCode = () =>
  useSmartMutation(
    (payload: any) => mxValidationsApi.postValidationCode(payload),
    {
      onSuccess: () => {
        toast({
          variant: "default",
          title: "Success",
          description: "Code added successfully.",
        });

        queryClient.refetchQueries({
          queryKey: ["mx", "validation-code"],
        });
      },
    }
  );

export const useDeleteValidationCode = () =>
  useSmartMutation(
    ({ id, remoteUtilityId }: { id: number; remoteUtilityId: string }) =>
      mxValidationsApi.deleteValidationCode(id, remoteUtilityId),
    {
      onSuccess: () => {
        toast({
          variant: "default",
          title: "Success",
          description: "Code deleted successfully.",
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "validation-code"],
        });
      },
    }
  );

export const useGetRoutes = (
  queryParams: Record<string, any> = {},
  cycleIds: number[]
) => {
  return useSmartQuery(
    [QueryKeyFactory.module.mx.validations.routeList(cycleIds), queryParams],
    () => mxValidationsApi.getRoutes(queryParams, cycleIds),
    { enabled: !!cycleIds.length }
  );
};

export const useRoutesListForNavigation = (
  queryParams: Record<string, any> = {},
  remoteUtilityId?: string
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.validations.routesListNavigation(),
    () =>
      mxValidationsApi.getRoutesListForNavigation({
        remoteUtilityId: String(remoteUtilityId),
        page: queryParams.page || 1,
        limit: queryParams.limit || 50,
        isBaseInfo: queryParams.is_base_info ?? true,
        isPaginationRequired: queryParams.is_pagination_required ?? true,
      }),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useGetMeterReaders = (queryParams: Record<string, any> = {}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.validations.meterReaders(),
    () => mxValidationsApi.getMeterReaders(queryParams)
  );
};

export const useAssignReader = () => {
  const { toast } = useToast();
  return useSmartMutation(
    ({ id, readerId }: { id: number; readerId: number[] }) =>
      mxValidationsApi.assignMeterReader(id, readerId),
    {
      onSuccess: () => {
        toast({ title: "Reader Assigned Successfully" });
      },
    }
  );
};

export const useDuplicateReadings = ({
  remoteUtilityId,
  meterRunId,
  page,
  limit,
  searchData = "",
  premises,
  sortBy,
  readingId,
}: {
  remoteUtilityId: string;
  meterRunId: string;
  page: number;
  limit: number;
  searchData?: string;
  premises?: string[];
  sortBy?: string[];
  readingId?: any;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.validations.duplicateReadings(
      meterRunId,
      page,
      searchData,
      premises,
      sortBy,
      readingId
    ),
    () =>
      mxValidationsApi.getDuplicateReadings({
        remoteUtilityId,
        meterRunId,
        page,
        limit,
        searchData,
        premises,
        sortBy,
      }),
    {
      enabled: !!meterRunId,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    }
  );
};

export const useReplaceDuplicateReading = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (data: any) => mxValidationsApi.replaceDuplicateReading(data),
    {
      onSuccess: () => {
        // queryClient.refetchQueries({
        //   queryKey: ["mx", "validation-break-run-count"],
        // });
        // queryClient.refetchQueries({
        //   queryKey: ["mx", "view-readings-list"],
        // });
        // queryClient.refetchQueries({
        //   queryKey: ["mx", "exemptions"],
        // });
      },
      onError: (error) => {
        toast({ title: error.message });
      },
    }
  );
};

export const usePutEstimateReadings = (meterRunId: string) =>
  useSmartMutation((payload: any) =>
    mxValidationsApi.putEstimateReadings(meterRunId, payload,
    ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["mx", "validation-break-run-count"],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "view-readings-list"],
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "missing-readings-list"],
        });
         queryClient.invalidateQueries({
          queryKey: ["mx", "exemptions"],
        });
      },
      onError: (error) => {
        toast({
          variant: "destructive",
          description:
            error.message
        });
      },
    }
  );